import FormLayout from "components/form/FormLayout";
import Toggle from "components/form/toggle/Toggle";
import React from "react";
import { useTranslation } from "react-i18next";
import { Select } from "components/select/index";
import Divider from "components/divider";
import { IPutTeeSheetSettings } from "api/rpc/2024-04/facilityAdmin/facility/settings";
import { useAppSelector } from "hooks/redux";

interface IProps {
  state: IPutTeeSheetSettings;
  updateState: <K extends keyof IPutTeeSheetSettings>(key: K, value: IPutTeeSheetSettings[K]) => void;
}

export default function DisplaySettings(props: IProps) {
  const { state, updateState } = props;
  const { t } = useTranslation();
  const { Option } = Select;
  const { facilityStore } = useAppSelector(store => store);

  function handleToggleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, checked } = event.target;
    updateState(id as keyof IPutTeeSheetSettings, checked);
  }
  return (
    <div className="p-2">
      <FormLayout>
        <FormLayout.Group>
          <Toggle
            labelRight={t("Enable web booking")}
            id="enable_web_booking"
            checked={!!state.enable_web_booking || false}
            onChange={handleToggleChange}
          />
        </FormLayout.Group>
        <FormLayout.Group>
          <Toggle
            labelRight={t("Display online pricing")}
            labelSupport={t("Display prices on the public booking portal")}
            id="display_online_pricing"
            checked={!!state.display_online_pricing || false}
            onChange={handleToggleChange}
          />
        </FormLayout.Group>
        <FormLayout.Group>
          <Toggle
            labelRight={t("Tee time access code")}
            labelSupport={t("Require an access code to view and book tee times")}
            id="tee_times_access_code"
            checked={!!state.tee_times_access_code || false}
            onChange={handleToggleChange}
          />
        </FormLayout.Group>
        {facilityStore?.facility?.module_waitlist ? (
          <FormLayout.Group>
            <Toggle
              labelRight={t("Display Waitlist")}
              labelSupport={t("Display waitlist on the public booking portal")}
              id="booking_engine_display_waitlist"
              checked={!!state.booking_engine_display_waitlist || false}
              onChange={handleToggleChange}
            />
          </FormLayout.Group>
        ) : null}

        <FormLayout.Group>
          <Toggle
            labelRight={t("Display Divisions Online")}
            labelSupport={t("Display the divisions for the tee sheet online")}
            id="display_divisions_online"
            checked={!!state.display_divisions_online || false}
            onChange={handleToggleChange}
          />
        </FormLayout.Group>
        <FormLayout.Group>
          <Select
            label={t("Web tee time display")}
            defaultValue={state.web_tee_times_display ?? ""}
            onChange={(value: "list" | "tee_sheet") => updateState("web_tee_times_display", value)}
            helpText={t("Choose the layout for tee times on the public site")}
          >
            {[
              { label: t("List"), value: "list" },
              { label: t("Tee Sheet"), value: "tee_sheet" },
            ].map((option, index) => (
              <Option key={index} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </FormLayout.Group>
        <FormLayout.Group>
          <Toggle
            labelRight={t("Show Full Tee Times Online")}
            labelSupport={t(
              "Display tee times that are already fully booked instead of hiding them from the online tee sheet",
            )}
            id="web_display_full_tee_times"
            checked={!!state.web_display_full_tee_times || false}
            onChange={handleToggleChange}
          />
        </FormLayout.Group>
      </FormLayout>
      <Divider />
      <FormLayout>
        <FormLayout.Group>
          <Toggle
            labelRight={t("Display customer types")}
            labelSupport={t("Show the customer type (e.g., Member) under their name")}
            id="display_customer_type"
            checked={!!state.display_customer_type || false}
            onChange={handleToggleChange}
          />
        </FormLayout.Group>
        <FormLayout.Group>
          <Toggle
            labelRight={t("Display booking graph")}
            labelSupport={t("Display a capacity graph at the top of the tee sheet")}
            id="display_booking_graph"
            checked={!!state.display_booking_graph || false}
            onChange={handleToggleChange}
          />
        </FormLayout.Group>
        <FormLayout.Group>
          <Toggle
            labelRight={t("Display booking source")}
            labelSupport={t("Show a source selector on new and edit booking modals for phone or walk-in bookings")}
            id="display_booking_source"
            checked={!!state.display_booking_source || false}
            onChange={handleToggleChange}
          />
        </FormLayout.Group>
        <FormLayout.Group>
          <Toggle
            labelRight={t("Tee sheet overview")}
            labelSupport={t("Shows a compact list of tee times on the left of the tee sheet")}
            id="tee_sheet_overview"
            checked={!!state.tee_sheet_overview || false}
            onChange={handleToggleChange}
          />
        </FormLayout.Group>
        <FormLayout.Group>
          <Toggle
            labelRight={t("Display turn division code")}
            labelSupport={t("Show turn labels (e.g., F-B) next to each tee time")}
            id="display_turn_division_code"
            checked={!!state.display_turn_division_code || false}
            onChange={handleToggleChange}
          />
        </FormLayout.Group>
      </FormLayout>
    </div>
  );
}
